import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Person,
  Business,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  Description,
  Assessment,
  NavigateNext,
} from '@mui/icons-material';
import api from '../utils/axios';
import dayjs from 'dayjs';
import LocationMap from '../components/LocationMap';

interface Client {
  id: string;
  type: 'INDIVIDUAL' | 'COMPANY';
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email: string;
  phone?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  claims?: Claim[];
  appointments?: Appointment[];
}

interface Claim {
  id: string;
  claimNumber: string;
  file_number: string;
  status: string;
  type: string;
  amount?: number;
  description?: string;
  dateOfLoss?: string;
  dateReported?: string;
  createdAt: string;
}

interface Appointment {
  id: string;
  title?: string;
  description?: string;
  startTime: string;
  endTime?: string;
  createdAt: string;
}

const ClientDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Client>>({});

  const { data: clientData, isLoading, error, refetch } = useQuery({
    queryKey: ['client', id],
    queryFn: async (): Promise<{ success: boolean; data: Client }> => {
      const response = await api.get<{ success: boolean; data: Client }>(`/clients/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  const client = clientData?.data;

  useEffect(() => {
    if (client) {
      setEditForm({
        type: client.type,
        firstName: client.firstName,
        lastName: client.lastName,
        companyName: client.companyName,
        email: client.email,
        phone: client.phone,
        address: client.address,
        status: client.status,
      });
    }
  }, [client]);

  const handleInputChange = (field: keyof Client, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await api.put(`/clients/${id}`, editForm);
      setIsEditDialogOpen(false);
      refetch();
    } catch (error) {
      console.error('Error updating client:', error);
    }
  };

  const handleCancel = () => {
    setIsEditDialogOpen(false);
    if (client) {
      setEditForm({
        type: client.type,
        firstName: client.firstName,
        lastName: client.lastName,
        companyName: client.companyName,
        email: client.email,
        phone: client.phone,
        address: client.address,
        status: client.status,
      });
    }
  };

  const getClientName = () => {
    if (!client) return '';
    return client.type === 'COMPANY' 
      ? client.companyName 
      : `${client.firstName} ${client.lastName}`;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !client) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load client details. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Breadcrumb */}
      <Breadcrumbs 
        separator={<NavigateNext fontSize="small" />} 
        sx={{ mb: 2 }}
      >
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/clients')}
          sx={{ cursor: 'pointer' }}
        >
          Clients
        </Link>
        <Typography color="text.primary">{getClientName()}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/clients')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          {getClientName()}
        </Typography>
        <Box ml="auto">
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => setIsEditDialogOpen(true)}
          >
            Edit Client
          </Button>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap={3}>
        <Box display="flex" gap={3} flexWrap="wrap">
          {/* Basic Information */}
          <Box flex="1" minWidth="300px">
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  {client.type === 'COMPANY' ? <Business sx={{ mr: 1 }} /> : <Person sx={{ mr: 1 }} />}
                  <Typography variant="h6">
                    {client.type === 'COMPANY' ? 'Company Information' : 'Personal Information'}
                  </Typography>
                </Box>
                
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      {client.type === 'COMPANY' ? 'Company Name' : 'Full Name'}
                    </Typography>
                    <Typography variant="body1">{getClientName()}</Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Client Type
                    </Typography>
                    <Chip 
                      label={client.type} 
                      color={client.type === 'COMPANY' ? 'primary' : 'secondary'}
                      size="small"
                    />
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Email
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <Email sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body1">{client.email}</Typography>
                    </Box>
                  </Box>
                  
                  {client.phone && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Phone
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Phone sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body1">{client.phone}</Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {client.address && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Address
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <LocationOn sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body1">{client.address}</Typography>
                      </Box>
                      <Box mt={2}>
                        <LocationMap
                          latitude={client.latitude}
                          longitude={client.longitude}
                          address={client.address}
                          title="Client Location"
                          height={250}
                          showAddress={false}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Statistics */}
          <Box flex="1" minWidth="300px">
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Assessment sx={{ mr: 1 }} />
                  <Typography variant="h6">Statistics</Typography>
                </Box>
                
                <Box display="flex" gap={2}>
                  <Box flex="1" textAlign="center">
                    <Typography variant="h4" color="primary">
                      {client.claims?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Claims
                    </Typography>
                  </Box>
                  <Box flex="1" textAlign="center">
                    <Typography variant="h4" color="secondary">
                      {client.appointments?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Appointments
                    </Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Status
                  </Typography>
                  <Chip 
                    label={client.status} 
                    color={client.status === 'ACTIVE' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                
                <Box mt={2}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Created
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <CalendarToday sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">
                      {dayjs(client.createdAt).format('MMM DD, YYYY')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Claims */}
        <Box>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Description sx={{ mr: 1 }} />
                <Typography variant="h6">Claims</Typography>
              </Box>
              
              {client.claims && client.claims.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Claim Number</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Date of Loss</TableCell>
                        <TableCell>Date Reported</TableCell>
                        <TableCell>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {client.claims.map((claim) => (
                        <TableRow key={claim.id}>
                          <TableCell>
                            <Button
                              variant="text"
                              onClick={() => navigate(`/claims/${claim.file_number}`)}
                            >
                              {claim.claimNumber}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={claim.status}
                              color={
                                claim.status === 'ACTIVE' ? 'success' :
                                claim.status === 'PENDING' ? 'warning' : 'default'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{claim.type}</TableCell>
                          <TableCell>
                            {claim.amount ? `$${claim.amount.toLocaleString()}` : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {claim.dateOfLoss ? dayjs(claim.dateOfLoss).format('MM/DD/YYYY') : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {claim.dateReported ? dayjs(claim.dateReported).format('MM/DD/YYYY') : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {dayjs(claim.createdAt).format('MM/DD/YYYY')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No claims found for this client.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Appointments */}
        <Box>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <CalendarToday sx={{ mr: 1 }} />
                <Typography variant="h6">Appointments</Typography>
              </Box>
              
              {client.appointments && client.appointments.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>End Date</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {client.appointments.map((appointment) => (
                        <TableRow key={appointment.id}>
                          <TableCell>{appointment.title || 'N/A'}</TableCell>
                          <TableCell>
                            {dayjs(appointment.startTime).format('MM/DD/YYYY HH:mm')}
                          </TableCell>
                          <TableCell>
                            {appointment.endTime ? dayjs(appointment.endTime).format('MM/DD/YYYY HH:mm') : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {appointment.description || 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No appointments found for this client.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Client</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <FormControl fullWidth>
              <InputLabel>Client Type</InputLabel>
              <Select
                value={editForm.type || ''}
                label="Client Type"
                onChange={(e) => handleInputChange('type', e.target.value)}
              >
                <MenuItem value="INDIVIDUAL">Individual</MenuItem>
                <MenuItem value="COMPANY">Company</MenuItem>
              </Select>
            </FormControl>
            
            {editForm.type === 'COMPANY' ? (
              <TextField
                label="Company Name"
                value={editForm.companyName || ''}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                fullWidth
                required
              />
            ) : (
              <>
                <TextField
                  label="First Name"
                  value={editForm.firstName || ''}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  fullWidth
                  required
                />
                <TextField
                  label="Last Name"
                  value={editForm.lastName || ''}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  fullWidth
                  required
                />
              </>
            )}
            
            <TextField
              label="Email"
              type="email"
              value={editForm.email || ''}
              onChange={(e) => handleInputChange('email', e.target.value)}
              fullWidth
              required
            />
            
            <TextField
              label="Phone"
              value={editForm.phone || ''}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              fullWidth
            />
            
            <TextField
              label="Address"
              value={editForm.address || ''}
              onChange={(e) => handleInputChange('address', e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.status || ''}
                label="Status"
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              margin="dense"
              label="Latitude"
              type="number"
              fullWidth
              value={editForm.latitude ?? ''}
              onChange={e => handleInputChange('latitude', parseFloat(e.target.value))}
            />
            <TextField
              margin="dense"
              label="Longitude"
              type="number"
              fullWidth
              value={editForm.longitude ?? ''}
              onChange={e => handleInputChange('longitude', parseFloat(e.target.value))}
            />
            <Box mt={2}>
              <LocationMap
                latitude={editForm.latitude}
                longitude={editForm.longitude}
                address={editForm.address}
                title="Set Client Location"
                height={250}
                editable
                onLocationSelect={(lat, lng) => {
                  setEditForm(prev => ({ ...prev, latitude: lat, longitude: lng }));
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientDetails; 